import { createSelector, createSlice } from "@reduxjs/toolkit"
import {
  CUSTOMER_ACCESS_TOKEN_KEY,
  DELIVERY_OPTIONS,
  LAST_CART_ID_KEY,
  LAST_CHECKOUT_ID_KEY,
} from "src/app/constants"

export function getHawaiiShippingAddress(shippingAddress) {
  return {
    company: "",
    ...shippingAddress,
    province: "Hawaii",
    country: "United States",
  }
}

/**
 * Returns saved customer access token from localStorage.
 * @returns Object { accessToken, expiresAt } or null
 */
function getCustomerAccessToken() {
  let token = localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY)
  if (token) {
    token = JSON.parse(atob(token))
  }
  return token
}

const initialState = {
  lastCheckoutId: localStorage.getItem(LAST_CHECKOUT_ID_KEY),
  lastCartId: localStorage.getItem(LAST_CART_ID_KEY),
  customerAccessToken: getCustomerAccessToken(),
  customer: {
    id: null,
  },
  checkout: {
    id: null,
  },
  cart: {
    id: null,
  },
  options: {
    deliveryOption: null,
    zip: null,
    zipData: null,
    specialEvent: null,
    pickupLocation: null,
  },
  config: {
    addons: [],
    nextDayProducts: [],
    specialProducts: [],
    vendorSchedules: [],
    volumeDiscountProducts: [],
    checkoutProductVendors: [],
  },
  showOrderSummary: false,
  openAllProductsDialog: false,
  allProductsSearchQuery: "",
  activeProduct: null,
  activeProductQuantity: 1,
  selectedShopPolicy: null,
  cartJsLineItems: [],
  lineItemsUpdated: false,
  appliedGiftCards: [],
}

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setCustomer: (state, action) => {
      state.customer = { ...state.customer, ...action.payload }
    },
    setLastCheckoutId: (state, action) => {
      state.lastCheckoutId = action.payload
      localStorage.setItem(LAST_CHECKOUT_ID_KEY, action.payload)
    },
    setCheckout: (state, action) => {
      state.checkout = { ...state.checkout, ...action.payload }
    },
    clearCheckout: (state) => {
      state.checkout = null
      localStorage.removeItem(LAST_CHECKOUT_ID_KEY)
    },
    setLastCartId: (state, action) => {
      state.lastCartId = action.payload
      localStorage.setItem(LAST_CART_ID_KEY, action.payload)
    },
    setCart: (state, action) => {
      console.log('setCart', { ...state.cart, ...action.payload })
      state.cart = { ...state.cart, ...action.payload }
    },
    clearCart: (state) => {
      state.checkout = null
      localStorage.removeItem(LAST_CART_ID_KEY)
    },
    setCheckoutConfig: (state, action) => {
      const {
        addons,
        nextday_products,
        special_products,
        vendor_schedules,
        volume_discount_products,
      } = action.payload

      state.config = {
        ...state.config,
        addons: addons || [],
        nextDayProducts: nextday_products || [],
        specialProducts: special_products || [],
        vendorSchedules: vendor_schedules || [],
        volumeDiscountProducts: volume_discount_products || [],
      }
    },
    setCheckoutProductVendors: (state, action) => {
      state.config = {
        ...state.config,
        checkoutProductVendors: action.payload,
      }
    },
    setCheckoutOption: (state, action) => {
      state.options = { ...state.options, ...action.payload }
    },
    toggleShowOrderSummary: (state) => {
      state.showOrderSummary = !state.showOrderSummary
    },
    setSelectedShopPolicy: (state, action) => {
      state.selectedShopPolicy = action.payload
    },
    setCartJsLineItems: (state, action) => {
      state.cartJsLineItems = action.payload
    },
    setLineItemsUpdated: (state, action) => {
      state.lineItemsUpdated = action.payload
    },
    setOpenAllProductsDialog: (state, action) => {
      state.openAllProductsDialog = action.payload
    },
    setAllProductsSearchQuery: (state, action) => {
      state.allProductsSearchQuery = action.payload
    },
    setActiveProduct: (state, action) => {
      state.activeProduct = action.payload
    },
    setActiveProductQuantity: (state, action) => {
      state.activeProductQuantity = action.payload
    },
    applyGiftCard: (state, action) => {
      if (!state.appliedGiftCards.includes(action.payload)) {
        state.appliedGiftCards = [...state.appliedGiftCards, action.payload]
      }
    },
    removeGiftCard: (state, action) => {
      if (state.appliedGiftCards.includes(action.payload)) {
        state.appliedGiftCards = state.appliedGiftCards.filter((gc => gc !== action.payload))
      }
    },
  },
})

/**
 * Returns the discounted total price of Checkout's lineItems.
 */
export const lineItemsDiscountedSubTotalPriceSelector = createSelector(
  [
    (state) => state.checkout.cart.items,
    (state) => state.checkout.cart.cost.subtotalAmount,
  ],
  (lineItems, subtotalAmount) => {
    let totalDiscountAmount = 0

    lineItems?.edges.forEach((lineItem) => {
      // Loop through discount allocations in the current line item
      lineItem.node.discountAllocations.forEach(
        ({ discountedAmount, discountApplication }) => {
          if (
            discountApplication.targetSelection === "ENTITLED" &&
            discountApplication.targetType === "LINE_ITEM"
          ) {
            // Add the allocated amount to the total discount amount
            totalDiscountAmount += parseFloat(discountedAmount.amount)
          }
        },
      )
    })

    // Subtract the subtotalAmount with totalDiscountAmount
    return parseFloat(subtotalAmount?.amount || 0) - totalDiscountAmount
  },
)

/**
 * Returns true if a Free Shipping discount code has been applied to Checkout, else returns false.
 */
export const hasFreeShippingDiscountCodeSelector = createSelector(
  [(state) => state.checkout.cart.discountAllocations],
  (discountAllocations) => {
    let appliedFreeShippingDiscountCode = false

    discountAllocations?.forEach((discountApplication) => {
      if (
        discountApplication.node.targetSelection === "ALL" &&
        discountApplication.node.targetType === "SHIPPING_LINE"
      ) {
        appliedFreeShippingDiscountCode = true
      }
    })

    return appliedFreeShippingDiscountCode
  },
)

/**
 * The total ($) amount of Gift Cards to be purchased.
 */
export const giftCardsTotalAmountSelector = createSelector(
  [(state) => state.checkout.cart.lines],
  (lineItems) => {
    let giftCardsTotalAmount = 0

    lineItems?.edges.forEach((lineItem) => {
      if (lineItem.node.merchandise?.product.productType?.includes("Gift Card")) {
        giftCardsTotalAmount +=
          lineItem.node.quantity *
          parseFloat(lineItem.node.merchandise?.price.amount)
      }
    })

    return giftCardsTotalAmount
  },
)

/**
 * Returns true if lineItems has a Beer or Seltzer item.
 */
export const hasBeerOrSeltzerItemsSelector = createSelector(
  [
    (state) => state.checkout.options.deliveryOption,
    (state) => state.checkout.cart.lines,
  ],
  (deliveryOption, lineItems) => {
    let hasBeerOrSeltzerItems = false

    lineItems?.edges.forEach((lineItem) => {
      const underBeerAndSeltzersCollection = ["Beer", "Seltzer", "Mixed Drinks"].includes(lineItem.node.merchandise?.product.productType);

      if (
        deliveryOption === DELIVERY_OPTIONS.US_MAINLAND_SHIPPING &&
        underBeerAndSeltzersCollection &&
        !lineItem.node.merchandise.product.title.includes(" Cans") && // Allow Canned Beer or Seltzer items.
        !hasBeerOrSeltzerItems
      ) {
        hasBeerOrSeltzerItems = true
      } else if (
        deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING &&
        underBeerAndSeltzersCollection && !hasBeerOrSeltzerItems
      ) {
        hasBeerOrSeltzerItems = true
      }
    })

    return hasBeerOrSeltzerItems
  },
)

/**
 * The ($) amount that the user needs to add to his/her cart to get a free shipping rate.
 */
export const amountForFreeShippingSelector = createSelector(
  [
    hasFreeShippingDiscountCodeSelector,
    giftCardsTotalAmountSelector,
    (state) => state.checkout.cart.cost.subtotalAmount,
    (state) => state.checkout.cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption,
    (state) => state.checkout.options.zipData,
  ],
  (
    hasFreeShippingDiscountCode,
    giftCardsTotalAmount,
    lineItemsSubtotalPrice,
    selectedDeliveryOption,
    zipData,
  ) => {
    let amountForFreeShipping = 0

    if (
      !zipData?.rates ||
      // parseFloat(selectedDeliveryOption?.estimatedCost.amount) === 0 ||
      hasFreeShippingDiscountCode
    )
      return 0

    const zipFreeShippingRate = Object.values(zipData?.rates).find(
      (rate) => parseFloat(rate.rate_price) === 0,
    )

    if (!zipFreeShippingRate) return 0

    // if (parseInt(selectedDeliveryOption?.estimatedCost.amount) !== 0) {
    amountForFreeShipping =
      parseFloat(zipFreeShippingRate.minimum_rate) +
      giftCardsTotalAmount -
      parseFloat(lineItemsSubtotalPrice.amount)
    // }

    if (amountForFreeShipping <= 0) return 0

    return amountForFreeShipping || 0
  },
)

/**
 * Returns the required minimum amount the customer has to pay to enable delivery.
 */
export const minimumRateSelector = createSelector(
  [(state) => state.checkout.options.zipData],
  (zipData) => {
    if (!zipData?.rates) return 0

    return (
      Object.values(zipData.rates)
        .map((rate) => parseInt(rate.minimum_rate))
        ?.sort()[0] || 0
    )
  },
)

/**
 * Returns true if the customer's cart is above the required minimum amount to enable delivery, else returns false.
 */
export const aboveMinimumRateSelector = createSelector(
  [
    minimumRateSelector,
    giftCardsTotalAmountSelector,
    (state) => state.checkout.cart.cost.subtotalAmount,
  ],
  (minimumRate, giftCardsTotalAmount, subtotalAmount) => {
    if (minimumRate <= 0) return true

    const subtotal =
      parseFloat(subtotalAmount?.amount || 0) -
      parseFloat(giftCardsTotalAmount)

    return subtotal > minimumRate
  },
)

export const otherCheckoutConfigSelector = createSelector(
  [
    (state) => state.checkout.cart.lines,
    (state) => state.checkout.config,
  ],
  (lineItems, config) => {
    const customLabelProductIds = []
    const customDeliveryStartDateProductIds = []
    const customLabelProducts = []
    const customStartDateProducts = []
    let hasNextDayProducts = false

    config.specialProducts.forEach((specialProduct) => {
      if (specialProduct.custom_label_enabled)
        customLabelProductIds.push(specialProduct.variant_id)
      if (specialProduct.delivery_start_date)
        customDeliveryStartDateProductIds.push(specialProduct.variant_id)
    })

    lineItems?.edges.map((edge) => {
      if (!hasNextDayProducts) {
        hasNextDayProducts = config.nextDayProducts.find(
          (nextDayProduct) =>
            nextDayProduct.product_id === edge.node.merchandise?.product?.id,
        )
          ? true
          : false
      }

      if (!hasNextDayProducts) {
        hasNextDayProducts = config.vendorSchedules.find(
          (vendorSchedule) =>
            config.checkoutProductVendors.includes(
              vendorSchedule.vendor.name,
            ) && vendorSchedule.next_day_delivery,
        )
          ? true
          : false
      }

      if (customLabelProductIds.includes(edge.node.merchandise?.id))
        customLabelProducts.push(edge.node)

      if (customDeliveryStartDateProductIds.includes(edge.node.merchandise?.id))
        customStartDateProducts.push({
          ...edge.node,
          delivery_start_date: config.specialProducts.find(
            (specialProduct) =>
              specialProduct.variant_id === edge.node.merchandise?.id,
          )?.delivery_start_date,
        })
    })

    const checkoutVendorSchedules = config.vendorSchedules.filter(
      (vendorSchedule) =>
        config.checkoutProductVendors.includes(vendorSchedule.vendor.name),
    )

    return {
      hasNextDayProducts,
      checkoutVendorSchedules,
      customLabelProducts,
      customStartDateProducts,
    }
  },
)

export const shouldAddHawaiiGetSelector = createSelector(
  [
    (state) => state.checkout.options.deliveryOption,
    (state) => state.checkout.cart.lines,
  ],
  (deliveryOption, lineItems) => {
    if (deliveryOption !== DELIVERY_OPTIONS.US_MAINLAND_SHIPPING) return false

    const hawaiiGetLineItem = lineItems.edges.find((item => item.node.title === 'General Excise Tax'))

    if (!hawaiiGetLineItem) return true

    return false;
  },
)

export const shouldAddOrUpdateHawaiiGetSelector = createSelector(
  [
    (state) => state.checkout.options.deliveryOption,
    (state) => state.checkout.cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption,
    (state) => state.checkout.cart.lines,
    (state) => state.checkout.cart.cost.totalAmount.amount,
  ],
  (deliveryOption, selectedDeliveryOption, lineItems, paymentDue) => {
    // Return false, if deliveryOption is not US_MAINLAND_SHIPPING.
    if (deliveryOption !== DELIVERY_OPTIONS.US_MAINLAND_SHIPPING) return false

    // Return false, if selectedDeliveryOption has not been set yet.
    if (!selectedDeliveryOption?.estimatedCost?.amount) return false

    const hawaiiGetLineItem = lineItems.edges.find((item => item.node.title === 'General Excise Tax'))

    // Return true if Hawaii GET has not been added yet.
    if (!hawaiiGetLineItem) return true

    const taxRate = 0.04712; // 4.712%
    const currentTaxAmount = parseFloat(hawaiiGetLineItem.node.merchandise.price.amount);
    const totalAmount = parseFloat(paymentDue) - currentTaxAmount;
    const expectedTaxAmount = (totalAmount * taxRate).toFixed(2);

    if (currentTaxAmount != expectedTaxAmount) {
      const difference = Math.abs(currentTaxAmount - expectedTaxAmount);

      // Return false if the difference of the current tax amount and our expected tax amount is less than 0.02.
      if (difference < 0.02) {
        return false
      }

      return true
    }

    return false
  },
)

export const {
  setCustomer,
  setLastCheckoutId,
  setCheckout,
  clearCheckout,
  setLastCartId,
  setCart,
  clearCart,
  setCheckoutConfig,
  setCheckoutProductVendors,
  loadCheckoutConfig,
  setCheckoutOption,
  toggleShowOrderSummary,
  setSelectedShopPolicy,
  setLineItemsUpdated,
  setOpenAllProductsDialog,
  setAllProductsSearchQuery,
  setActiveProduct,
  setActiveProductQuantity,
  applyGiftCard,
  removeGiftCard,
} = checkoutSlice.actions

export default checkoutSlice.reducer
